/**
 * Keuzedialoog bij «Container»-creatie (M8). Verschijnt direct nadat een
 * Container op het canvas is gedropt: de modelleur kiest een sub-stereotype
 * (bv. «Microservice», «Database») of laat 'm generiek. Een gekozen sub
 * vervangt `c4.Container` op het concept (de sub erft Container's attributen
 * via `extends_stereotype`, dus géén dubbele bundle).
 *
 * Async-los: de dialoog kent alleen `onChoose(subId | null)` + `onCancel`.
 * De Canvas patcht op keuze het net-aangemaakte concept.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveBilingual } from "../../registry";

interface Props {
  baseLabel: string;
  options: Array<{ id: string; label: Record<string, string> }>;
  lang: string;
  onChoose: (subId: string | null) => void;
  onCancel: () => void;
}

export function SubStereotypeChoiceModal({ baseLabel, options, lang, onChoose, onCancel }: Props) {
  const { t } = useTranslation();
  // null = generiek (behoud c4.Container); anders het gekozen sub-id.
  const [selected, setSelected] = useState<string | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sub-stereo-title"
    >
      <div
        className="w-[440px] rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-xl"
        role="dialog"
        aria-modal="true"
      >
        <header className="border-(--ld-border) border-b px-4 py-2">
          <h2 id="sub-stereo-title" className="font-semibold text-(--ld-text) text-sm">
            {t("subStereotype.title", { base: baseLabel })}
          </h2>
          <p className="mt-0.5 text-(--ld-muted) text-xs">{t("subStereotype.hint")}</p>
        </header>
        <div className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto px-4 py-3">
          <label className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-(--ld-raised)">
            <input
              type="radio"
              name="sub-stereo"
              checked={selected === null}
              onChange={() => setSelected(null)}
            />
            <span className="text-(--ld-text)">
              {t("subStereotype.generic", { base: baseLabel })}
            </span>
          </label>
          {options.map((opt) => (
            <label
              key={opt.id}
              className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-(--ld-raised)"
            >
              <input
                type="radio"
                name="sub-stereo"
                checked={selected === opt.id}
                onChange={() => setSelected(opt.id)}
              />
              <span className="text-(--ld-text)">«{resolveBilingual(opt.label, lang)}»</span>
            </label>
          ))}
        </div>
        <footer className="flex justify-end gap-2 border-(--ld-border) border-t px-4 py-2">
          <button
            type="button"
            className="rounded border border-(--ld-control) px-3 py-1 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
            onClick={onCancel}
          >
            {t("subStereotype.cancel")}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="rounded bg-(--ld-primary) px-3 py-1 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover)"
            onClick={() => onChoose(selected)}
          >
            {t("subStereotype.confirm")}
          </button>
        </footer>
      </div>
    </div>
  );
}
